import React, { useEffect, useState } from 'react';
import { useLocalization } from '../engine/localization/context';
import { generateQrDataUrl } from '../engine/actions/qr';
import { BrandingConfig, QrFeatureConfig } from '../types/profile';
import { X, Download, QrCode, ExternalLink, Sparkles } from 'lucide-react';

interface QrModalProps {
  isOpen: boolean;
  onClose: () => void;
  profileUrl: string;
  clientName: string;
  branding?: BrandingConfig;
  logoUrl?: string;
  qrConfig?: QrFeatureConfig;
}

export const QrModal: React.FC<QrModalProps> = ({
  isOpen,
  onClose,
  profileUrl,
  clientName,
  branding,
  logoUrl,
  qrConfig,
}) => {
  const { t } = useLocalization();
  const [qrSrc, setQrSrc] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && profileUrl) {
      setLoading(true);
      const isBranded = qrConfig?.branded !== false;
      const fgColor = qrConfig?.foreground || branding?.primaryColor || '#0284c7';
      const bgColor = qrConfig?.background || '#ffffff';
      const embedLogo: string | undefined = isBranded
        ? typeof qrConfig?.logo === 'string'
          ? qrConfig.logo
          : qrConfig?.logo !== false
          ? logoUrl
          : undefined
        : undefined;

      generateQrDataUrl(profileUrl, {
        width: 420,
        margin: 2,
        foreground: fgColor,
        background: bgColor,
        logoUrl: embedLogo,
        errorCorrectionLevel: 'H',
      })
        .then((url) => {
          setQrSrc(url);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, [isOpen, profileUrl, branding, logoUrl, qrConfig]);

  if (!isOpen) return null;

  const handleDownload = () => {
    if (!qrSrc) return;
    const link = document.createElement('a');
    link.href = qrSrc;
    link.download = `${clientName.replace(/\s+/g, '-').toLowerCase()}-qr.png`;
    link.click();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-sm animate-fade-in overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[min(90vw,380px)] rounded-3xl bg-slate-900 border border-sky-500/30 p-5 sm:p-6 shadow-2xl text-center text-white my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          aria-label={t('action.close')}
          className="absolute top-4 right-4 p-1.5 rounded-full bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Header */}
        <div className="flex items-center justify-center gap-2 mb-1.5 text-sky-400 font-bold text-base">
          <QrCode className="w-5 h-5" />
          <span>{t('qr.modalTitle')}</span>
        </div>

        <div className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-sky-500/10 border border-sky-500/30 text-sky-300 text-[10px] font-semibold mb-3">
          <Sparkles className="w-3 h-3 text-sky-400" />
          <span>{t('qr.brandedBadge') || 'Branded Vector QR'}</span>
        </div>

        <p className="text-xs text-slate-300 mb-4">
          {t('qr.instruction')}
        </p>

        {/* QR Code Display Container with subtle glow aura */}
        <div className="relative mx-auto w-[min(65vw,240px)] h-[min(65vw,240px)] p-3 bg-white rounded-2xl shadow-[0_10px_35px_rgba(0,0,0,0.5),0_0_30px_rgba(56,189,248,0.2)] border border-white/20 flex items-center justify-center mb-5">
          {loading ? (
            <div className="text-slate-500 text-xs font-medium animate-pulse">
              Generating QR...
            </div>
          ) : qrSrc ? (
            <img
              src={qrSrc}
              alt="QR Code"
              className="w-full h-full object-contain select-none"
            />
          ) : (
            <div className="text-red-500 text-xs font-medium">
              Failed to generate QR
            </div>
          )}
        </div>

        {/* Direct Link Preview */}
        <div className="text-[11px] text-slate-400 bg-slate-800/80 rounded-xl px-3 py-2 mb-5 break-all flex items-center justify-center gap-1.5 font-mono dir-ltr">
          <span className="truncate">{profileUrl}</span>
          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sky-400 hover:text-sky-300"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>

        {/* Actions: Download QR */}
        <button
          type="button"
          onClick={handleDownload}
          disabled={!qrSrc || loading}
          className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-sky-500 hover:bg-sky-400 text-white font-bold text-xs shadow-md transition-all active:scale-[0.98]"
        >
          <Download className="w-4 h-4" />
          <span>{t('action.downloadQr')}</span>
        </button>
      </div>
    </div>
  );
};

